import { useMutation, useQuery } from '@tanstack/react-query';
import { sharedQueryKeys } from '~/api/utils/sharedQueryKeys';
import { Translation } from '~/shared/types/translation';
import { BpmLocale } from '~/sites/beatsperminute/types/config';
import { api } from './utils/api-base';
import { reactQueryClient } from '~/shared/contexts/CommonAppContext';

const cacheTranslations = (translations: Translation[]) => {
  translations.forEach(cacheTranslation)
}

const cacheTranslation = (translation: Translation) => {
  const id: Id = { id: translation.id }
  const uniqueWithoutId: UniqueWithoutId = { locale: translation.locale, siteId: translation.site_id, label: translation.label };
  reactQueryClient.setQueryData([sharedQueryKeys.translations.show, id], translation);
  reactQueryClient.setQueryData([sharedQueryKeys.translations.show, uniqueWithoutId], translation);
}

type Id = {
  id: number;
};

type UniqueWithoutId = {
  locale: string
  siteId: number
  label: string
}

export type UseSearchTranslationsArgs = {
  site_id: number | 'all'
  labels_filter?: string
  text_filter?: string
  locales_filter?: string
  skip_cache?: boolean
}

export const useSearchTranslations = ({
  site_id,
  labels_filter = '',
  text_filter = '',
  locales_filter = '',
  skip_cache = false
}: UseSearchTranslationsArgs) => {
  return useQuery({
    queryKey: [sharedQueryKeys.translations.index, site_id, labels_filter, locales_filter, skip_cache, text_filter],
    queryFn: async () => {
      const response = await api.get<Translation[]>(`/translations`, {
        params: {
          site_id,
          labels_filter,
          text_filter,
          locales_filter,
          skip_cache,
        },
      });
      cacheTranslations(response.data);
      return response.data;
    },
    staleTime: Infinity,
  });
}

export const useTranslation = (identifiers: Id | UniqueWithoutId) => {
  return useQuery({
    queryKey: [sharedQueryKeys.translations.show, identifiers],
    queryFn: async () => {
      const response = await api.get<Translation>(
        `/translations/fetch`, {
        params: identifiers,
      });
      return response.data;
    },
    staleTime: Infinity,
  }
  );
};


type UpdateTranslationParams = {
  text: string
}

export const useUpdateTranslation = ({ id }: { id: number }) => {
  return useMutation({
    mutationFn: async (params: UpdateTranslationParams) => {
      const response = await api.patch<Translation>(`/translations/${id}`, { translation: params });
      const translation = response.data;

      cacheTranslation(translation);
      return translation;
    }
  });
}


type CreateTranslationParams = {
  text: string
  label: string
  locale: BpmLocale
  site_id: number
};

export const useCreateTranslation = () => {
  return useMutation({
    mutationFn: async (params: CreateTranslationParams) => {
      const response = await api.post<Translation>('/translations', { translation: params });
      const translation = response.data;

      cacheTranslation(translation);
      return translation;
    }
  });
}

export const useDeleteTranslation = () => {
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/translations/${id}`);
    },
    onSuccess: () => {
      reactQueryClient.invalidateQueries({ queryKey: [sharedQueryKeys.translations.index,] });
    },
  });
};
